/**
 * ========================================
 * CHATBOT WIDGET - FACE SCAN MODULE
 * Handles Camera, Image Capture, and Analysis
 * ========================================
 */

(function () {
    'use strict';

    // State Management for Face Scan
    let cameraStream = null;
    let lightingAnalysisInterval = null;
    let faceCheckInterval = null;
    let isFaceAnalysisOk = false;
    let analysisFailureReason = "Positioning Face...";

    // DOM References (will be set explicitly)
    let faceScanContainer = null;
    let cameraPreview = null;
    let photoCanvas = null;
    let captureBtn = null;
    let uploadBtn = null;
    let retakeBtn = null;
    let confirmScanBtn = null;
    let capturedImageContainer = null;
    let capturedImage = null;
    let faceUploadInput = null;
    let skipScanBtn = null;
    let backToSelectionBtn = null;

    // Callbacks
    let onFaceScanComplete = null;
    let onFaceScanCancel = null;
    let onImageReady = null; // Called when image is ready (after capture/upload) so upload can start in background

    // Cached quality result so Submit shows good/error instantly (no re-analysis delay)
    let lastQualityResult = null;

    // Track video play for this page load (resets on refresh)
    let sessionPlayed = false;
    const getHasPlayed = () => sessionPlayed;
    const setHasPlayed = () => { sessionPlayed = true; };

    /**
     * Set DOM references dynamically
     */
    function setDOMReferences(refs) {
        faceScanContainer = refs.faceScanContainer;
        cameraPreview = refs.cameraPreview;
        photoCanvas = refs.photoCanvas;
        captureBtn = refs.captureBtn;
        uploadBtn = refs.uploadBtn;
        retakeBtn = refs.retakeBtn;
        confirmScanBtn = refs.confirmScanBtn;
        capturedImageContainer = refs.capturedImageContainer;
        capturedImage = refs.capturedImage;
        faceUploadInput = refs.faceUploadInput;
        skipScanBtn = refs.skipScanBtn;
        backToSelectionBtn = refs.backToSelectionBtn;

        // Bind internal events after DOM is set
        bindEvents();
    }

    function setCallbacks(callbacks) {
        if (callbacks.onComplete) onFaceScanComplete = callbacks.onComplete;
        if (callbacks.onCancel) onFaceScanCancel = callbacks.onCancel;
        if (callbacks.onImageReady) onImageReady = callbacks.onImageReady;
    }

    async function startFaceScan() {
        console.log("Starting Face Scan UI...");

        // Show face scan container
        if (faceScanContainer) {
            if (window.ChatbotViewController) {
                window.ChatbotViewController.showFaceScan();
            } else {
                faceScanContainer.style.display = 'flex';
            }

            // Show selection, hide camera
            const selectionStep = faceScanContainer.querySelector('#face-scan-selection');
            const cameraStep = faceScanContainer.querySelector('#face-scan-camera');
            if (selectionStep) {
                selectionStep.classList.add('active');
                selectionStep.style.display = 'flex';
            }
            if (cameraStep) {
                cameraStep.classList.remove('active');
                cameraStep.style.display = 'none';
            }

            if (confirmScanBtn) {
                confirmScanBtn.disabled = false;
                confirmScanBtn.textContent = "Submit";
            }

            // Ensure camera is stopped initially
            stopCamera();

            // Play video once when opening - UNMUTED
            const faceScanVideo = faceScanContainer.querySelector('#face-scan-video-player');
            if (faceScanVideo) {
                if (!getHasPlayed()) {
                    // Slight delay to ensure DOM is ready
                    setTimeout(() => {
                        console.log("[FaceScan] Initializing video playback...");

                        const attemptPlay = () => {
                            faceScanVideo.style.display = 'block';
                            faceScanVideo.muted = false;
                            faceScanVideo.volume = 1.0;
                            faceScanVideo.currentTime = 0;
                            faceScanVideo.onended = function () { this.currentTime = 0; this.pause(); };

                            const playPromise = faceScanVideo.play();
                            if (playPromise !== undefined) {
                                playPromise.then(() => {
                                    console.log("[FaceScan] Video playing successfully");
                                    setHasPlayed();
                                }).catch(err => {
                                    console.log("[FaceScan] Audio blocked, playing muted");
                                    faceScanVideo.muted = true;
                                    faceScanVideo.play().then(() => setHasPlayed());
                                });
                            }
                        };

                        if (faceScanVideo.readyState >= 3) {
                            attemptPlay();
                        } else {
                            faceScanVideo.oncanplay = () => {
                                faceScanVideo.oncanplay = null;
                                attemptPlay();
                            };
                            faceScanVideo.load();
                        }
                    }, 200);
                } else {
                    // Already played: Stop and hide
                    console.log("[FaceScan] Suppressing video playback (already played)");
                    faceScanVideo.pause();
                    faceScanVideo.style.display = 'none';
                    // Content will automatically move up due to flex layout
                }
            }
        }
    }

    /**
     * Show a resolution error banner ON the face scan selection screen
     * Called after API confirms image is not good enough
     */
    function showResolutionError(message) {
        if (!faceScanContainer) return;

        // Remove any existing banner
        const existing = faceScanContainer.querySelectorAll('.api-resolution-error');
        existing.forEach(el => el.remove());

        // Find which step is currently active
        const selectionStep = faceScanContainer.querySelector('#face-scan-selection');
        const cameraStep = faceScanContainer.querySelector('#face-scan-camera');

        // Target the active step or fallback to selection
        let targetStep = (cameraStep && cameraStep.style.display !== 'none') ? cameraStep : selectionStep;

        if (!targetStep) return;

        const banner = document.createElement('div');
        banner.className = 'api-resolution-error';
        banner.style.cssText = [
            'background: #fef2f2',
            'border: 1.5px solid #fca5a5',
            'border-radius: 12px',
            'padding: 8px 16px',
            'margin: 4px 0 8px',
            'text-align: center',
            'width: 100%',
            'box-sizing: border-box',
        ].join(';');

        banner.innerHTML = `
            <p style="margin:0;font-size:12px;color:#dc2626;font-weight:700;line-height:1.4;">
                ${message || 'Image is not clear, upload clear or closer image'}
            </p>
        `;

        // Insert after the header in the target step
        const header = targetStep.querySelector('.face-scan-header');
        if (header && header.nextSibling) {
            targetStep.insertBefore(banner, header.nextSibling);
        } else {
            targetStep.insertBefore(banner, targetStep.firstChild);
        }
        // Auto-remove after 15s
        setTimeout(() => { if (banner.parentNode) banner.remove(); }, 15000);
    }

    function resetSubmitButton() {
        if (confirmScanBtn) {
            confirmScanBtn.disabled = false;
            confirmScanBtn.textContent = 'Submit';
            confirmScanBtn.style.opacity = '1';
        }
    }

    /**
     * Start the actual camera feed
     */
    async function activateCamera() {
        console.log("Activating Camera...");

        // UI Transitions
        const selectionStep = faceScanContainer ? faceScanContainer.querySelector('#face-scan-selection') : null;
        const cameraStep = faceScanContainer ? faceScanContainer.querySelector('#face-scan-camera') : null;

        if (selectionStep) {
            selectionStep.classList.remove('active');
            selectionStep.style.display = 'none';
        }
        if (cameraStep) {
            cameraStep.classList.add('active');
            cameraStep.style.display = 'flex';

            const captureActions = cameraStep.querySelector('.capture-actions');
            const confirmActions = cameraStep.querySelector('.confirm-actions');
            if (captureActions) captureActions.style.display = 'flex';
            if (confirmActions) confirmActions.style.display = 'none';

            // Ensure overlay is shown for camera
            const overlay = cameraStep.querySelector('#camera-overlay');
            if (overlay) overlay.style.display = 'block';
        }

        if (capturedImageContainer) capturedImageContainer.style.display = 'none';
        if (cameraPreview) {
            cameraPreview.style.display = 'block';
            cameraPreview.style.opacity = '1';
        }

        // Check for secure context/capabilities
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            console.error("Camera API not available.");
            handleCameraError();
            return;
        }

        try {
            stopCamera();

            try {
                cameraStream = await navigator.mediaDevices.getUserMedia({
                    video: {
                        facingMode: 'user',
                        width: { ideal: 1280, min: 640 },
                        height: { ideal: 1280, min: 640 }
                    },
                    audio: false
                });
            } catch (e) {
                console.warn("Retrying with basic constraints...");
                cameraStream = await navigator.mediaDevices.getUserMedia({
                    video: true,
                    audio: false
                });
            }

            if (cameraPreview) {
                cameraPreview.srcObject = cameraStream;
                await new Promise((resolve) => {
                    cameraPreview.onloadedmetadata = () => resolve();
                    setTimeout(resolve, 1000);
                });
                await cameraPreview.play();
            }

            startFaceAnalysis();
            startFaceStatusCheck();

        } catch (err) {
            console.error("Camera access failed:", err);
            handleCameraError();
        }
    }

    function handleCameraError() {
        if (cameraPreview) cameraPreview.style.display = 'none';

        const camOverlay = faceScanContainer ? faceScanContainer.querySelector('#camera-overlay') : null;
        if (camOverlay) {
            camOverlay.innerHTML = '<div style="display:flex; flex-direction:column; align-items:center; justify-content:center; height:100%; color:white; text-align:center; padding:20px; background:rgba(0,0,0,0.6);"><p style="font-weight:700; margin-bottom:10px;">Camera unavailable</p><p style="font-size:12px; opacity:0.9;">Please use the "Upload from Gallery" button instead.</p></div>';
        }

        // Show a back to selection button if camera fails?
        // For now just keep them on camera screen with error
    }

    function stopCamera() {
        if (cameraStream) {
            cameraStream.getTracks().forEach(track => {
                track.stop();
                track.enabled = false;
            });
            cameraStream = null;
        }

        if (cameraPreview) {
            cameraPreview.pause();
            cameraPreview.srcObject = null;
            cameraPreview.load();
        }

        // Explicitly stop face scan video if it's playing
        if (faceScanContainer) {
            const faceScanVideo = faceScanContainer.querySelector('#face-scan-video-player');
            if (faceScanVideo) {
                faceScanVideo.pause();
            }
        }

        if (lightingAnalysisInterval) clearInterval(lightingAnalysisInterval);
        if (faceCheckInterval) clearInterval(faceCheckInterval);
    }

    /**
     * Internal Loop: Analyzes lighting and status
     */
    function startFaceAnalysis() {
        const getCanvas = () => {
            const c = document.createElement('canvas');
            c.width = c.height = 100;
            return c;
        };
        const c = getCanvas();
        const ctx = c.getContext('2d', { willReadFrequently: true });

        lightingAnalysisInterval = setInterval(() => {
            if (!cameraStream || (faceScanContainer && faceScanContainer.style.display !== 'flex')) return;

            try {
                if (cameraPreview && cameraPreview.readyState === 4) {
                    ctx.drawImage(cameraPreview, 0, 0, 100, 100);
                    const data = ctx.getImageData(0, 0, 100, 100).data;

                    let brightnessSum = 0;
                    let rSum = 0, gSum = 0, bSum = 0;
                    let brightnesses = [];

                    for (let i = 0; i < data.length; i += 4) {
                        const r = data[i], g = data[i + 1], b = data[i + 2];
                        const avg = (0.299 * r + 0.587 * g + 0.114 * b);
                        brightnessSum += avg;
                        brightnesses.push(avg);
                        rSum += r; gSum += g; bSum += b;
                    }

                    const avgB = brightnessSum / 10000;
                    const avgR = rSum / 10000, avgG = gSum / 10000, avgB_ = bSum / 10000;

                    let variance = 0;
                    brightnesses.forEach(b => { variance += Math.pow(b - avgB, 2); });
                    const stdDev = Math.sqrt(variance / 10000);

                    // RESTORED STRICTOR CHECKS as requested: Ensure face is clear and lighting is good
                    if (avgB < 90) {
                        isFaceAnalysisOk = false;
                        analysisFailureReason = "Too Dark - Move to Better Light";
                    } else if (avgB > 225) {
                        isFaceAnalysisOk = false;
                        analysisFailureReason = "Too Bright - Avoid Direct Sun";
                    } else if (stdDev < 20) {
                        isFaceAnalysisOk = false;
                        analysisFailureReason = "Position Face Closer to Frame";
                    } else if (avgR < avgG * 0.94 || avgR < avgB_ * 0.94) {
                        // RE-ENABLED RGB check for 'Lighting Too Cold'
                        isFaceAnalysisOk = false;
                        analysisFailureReason = "Lighting Too Cold or Obscured";
                    } else {
                        isFaceAnalysisOk = true;
                        analysisFailureReason = "Perfect! Hold Still...";
                    }
                }
            } catch (e) { isFaceAnalysisOk = true; }
        }, 400);
    }

    function startFaceStatusCheck() {
        faceCheckInterval = setInterval(() => {
            if (!faceScanContainer || faceScanContainer.style.display !== 'flex') {
                clearInterval(faceCheckInterval);
                return;
            }

            const faceOutline = faceScanContainer.querySelector('.face-outline');
            const statusBadge = faceScanContainer.querySelector('.face-status-badge');
            const statusText = statusBadge ? statusBadge.querySelector('.status-text') : null;

            if (isFaceAnalysisOk) {
                if (faceOutline) faceOutline.classList.add('active');
                if (statusBadge) statusBadge.classList.add('ready');
                if (statusText) statusText.textContent = "Perfect! Keep Still";
                if (captureBtn) {
                    captureBtn.disabled = false;
                    captureBtn.style.opacity = '1';
                }
            } else {
                if (faceOutline) faceOutline.classList.remove('active');
                if (statusBadge) statusBadge.classList.remove('ready');
                // Show generic hint only; specific errors (too dark, move closer) show only on Submit
                if (statusText) statusText.textContent = "Position your face in the circle";
                if (captureBtn) {
                    captureBtn.disabled = true;
                    captureBtn.style.opacity = '0.5';
                }
            }
        }, 300);
    }



    /**
     * Get full-resolution image data from video (no resize, no compression — pure for API).
     */
    function getRawImageData(source, w, h) {
        // Preserve more detail for the Face API quality gate.
        // We still cap for payload safety, but higher than before to avoid 101 "not enough resolution".
        const maxDim = 2048;
        let finalW = w;
        let finalH = h;
        
        if (finalW > maxDim || finalH > maxDim) {
            if (finalW > finalH) {
                finalH = Math.round((finalH * maxDim) / finalW);
                finalW = maxDim;
            } else {
                finalW = Math.round((finalW * maxDim) / finalH);
                finalH = maxDim;
            }
        }

        const canvas = document.createElement('canvas');
        canvas.width = finalW;
        canvas.height = finalH;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(source, 0, 0, finalW, finalH);
        // Higher quality to preserve edges for the scan model.
        return canvas.toDataURL('image/jpeg', 0.98);
    }

    function capturePhoto() {
        if (!cameraPreview) return;

        const w = cameraPreview.videoWidth;
        const h = cameraPreview.videoHeight;

        // No compression — full resolution, quality 1.0 for pure API results
        const rawData = getRawImageData(cameraPreview, w, h);

        if (capturedImage) {
            capturedImage.src = rawData;
            if (capturedImageContainer) capturedImageContainer.style.display = 'block';
        }

        // Run quality check now so Submit shows result instantly (no delay)
        lastQualityResult = null;
        analyzeUploadedImage(capturedImage, function (r) { lastQualityResult = r; });

        // Start upload in background so Submit feels instant (send raw to API)
        if (onImageReady && typeof onImageReady === 'function') onImageReady(rawData);

        // UI Transitions
        const cameraStep = faceScanContainer ? faceScanContainer.querySelector('#face-scan-camera') : null;
        if (cameraStep) {
            const captureActions = cameraStep.querySelector('.capture-actions');
            const confirmActions = cameraStep.querySelector('.confirm-actions');
            if (captureActions) captureActions.style.display = 'none';
            if (confirmActions) confirmActions.style.display = 'flex';
            const existingErr = cameraStep.querySelectorAll('.api-resolution-error');
            existingErr.forEach(el => el.remove());
        }

        stopCamera();
    }

    function analyzeUploadedImage(img, onResult) {
        const sampleSize = 100;
        const canvas = document.createElement('canvas');
        canvas.width = sampleSize;
        canvas.height = sampleSize;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });

        const doAnalysis = () => {
            // ── Read dimensions AFTER image is loaded ────────────────────────
            const naturalW = img.naturalWidth || img.width;
            const naturalH = img.naturalHeight || img.height;

            // 1. Reject truly tiny images
            if (naturalW < 400 || naturalH < 400) {
                onResult({ ok: false, reason: 'Image resolution is too low. Please upload a higher quality photo.' });
                return;
            }

            // 2. Sample the central face region (middle 50% wide, 60% tall)
            const srcX = Math.floor(naturalW * 0.25);
            const srcY = Math.floor(naturalH * 0.15);
            const srcW = Math.floor(naturalW * 0.50);
            const srcH = Math.floor(naturalH * 0.60);

            ctx.clearRect(0, 0, sampleSize, sampleSize);
            ctx.drawImage(img, srcX, srcY, srcW, srcH, 0, 0, sampleSize, sampleSize);
            const data = ctx.getImageData(0, 0, sampleSize, sampleSize).data;
            const pixelCount = sampleSize * sampleSize;

            let brightnessSum = 0, rSum = 0, gSum = 0, bSum = 0;
            const brightnesses = [];

            for (let i = 0; i < data.length; i += 4) {
                const r = data[i], g = data[i + 1], b = data[i + 2];
                const lum = 0.299 * r + 0.587 * g + 0.114 * b;
                brightnessSum += lum;
                brightnesses.push(lum);
                rSum += r; gSum += g; bSum += b;
            }

            const avgLum = brightnessSum / pixelCount;
            const avgR = rSum / pixelCount;
            const avgG = gSum / pixelCount;
            const avgBCh = bSum / pixelCount;

            let variance = 0;
            for (const lum of brightnesses) variance += (lum - avgLum) ** 2;
            const stdDev = Math.sqrt(variance / pixelCount);

            let ok = true, reason = '';

            if (avgLum < 40) { // Relaxed from 85 - allows dimmer but visible photos
                ok = false;
                reason = 'Image is too dark. Please take a photo in a better-lit area.';
            } else if (avgLum > 245) { // Relaxed from 230 - only block pure white washouts
                ok = false;
                reason = 'Image is too bright. Please avoid direct flash or very harsh light.';
            } else if (stdDev < 15) { // Relaxed from 30 - only block extremely blurry/far images
                ok = false;
                reason = 'Image appears too blurry or face is too far away. Please move closer.';
            }
            // Removed unnatural lighting check as it was causing false blocks for many users.

            onResult({ ok, reason });
        };

        if (img.complete && img.naturalWidth > 0) {
            doAnalysis();
        } else {
            img.onload = doAnalysis;
        }
    }

    function handleFileUpload(e) {
        if (!e.target.files || !e.target.files[0]) return;

        const file = e.target.files[0];
        const MAX_FILE_BYTES = 5 * 1024 * 1024; // 5MB safety cap for production stability
        // NOTE: For gallery uploads we intentionally do NOT resize/re-encode.
        // Re-encoding can drop effective quality enough to trigger "not enough resolution" from the Face API.

        // Defensive validation: stop early for unsupported types / huge files.
        try {
            if (file.type && !String(file.type).startsWith('image/')) {
                showResolutionError('Unsupported file type. Please upload an image (JPG/PNG).');
                return;
            }
            if (file.size && file.size > MAX_FILE_BYTES) {
                showResolutionError('Image is too large. Please upload a smaller image.');
                return;
            }
        } catch (err) {
            // If browser doesn't provide type/size, continue with existing flow.
        }

        const reader = new FileReader();

        reader.onload = function (evt) {
            const originalData = evt.target.result;
            const tempImg = new Image();

            // 1. SWITCH UI INSTANTLY for better perceived speed
            const selectionStep = faceScanContainer ? faceScanContainer.querySelector('#face-scan-selection') : null;
            const cameraStep = faceScanContainer ? faceScanContainer.querySelector('#face-scan-camera') : null;

            if (selectionStep) {
                selectionStep.classList.remove('active');
                selectionStep.style.display = 'none';
            }
            if (cameraStep) {
                cameraStep.classList.add('active');
                cameraStep.style.display = 'flex';

                const captureActions = cameraStep.querySelector('.capture-actions');
                const confirmActions = cameraStep.querySelector('.confirm-actions');

                // CRITICAL: DO NOT show live status badge for UPLOAD mode
                const statusBadge = cameraStep.querySelector('.face-status-badge');
                if (statusBadge) statusBadge.style.display = 'none';

                if (captureActions) captureActions.style.display = 'none';
                if (confirmActions) confirmActions.style.display = 'flex';
                if (cameraPreview) cameraPreview.style.display = 'none';

                const overlay = cameraStep.querySelector('#camera-overlay');
                if (overlay) overlay.style.display = 'none';

                // Clear any previous resolution error from last submit
                const existingErr = cameraStep.querySelectorAll('.api-resolution-error');
                existingErr.forEach(el => el.remove());
            }

            tempImg.onload = function () {
                if (capturedImage) {
                    // Keep original gallery image data as-is.
                    capturedImage.src = originalData;
                    capturedImage.style.display = 'block';
                }
                if (capturedImageContainer) {
                    capturedImageContainer.style.display = 'block';
                    capturedImageContainer.style.opacity = '1';
                }

                stopCamera();

                // Run quality check now so Submit shows result instantly (no delay)
                lastQualityResult = null;
                // Use tempImg (already loaded) for deterministic quality checks.
                analyzeUploadedImage(tempImg, function (r) { lastQualityResult = r; });

                // Start upload in background — send original, uncompressed to API
                if (onImageReady && typeof onImageReady === 'function') onImageReady(originalData);

                if (confirmScanBtn) {
                    confirmScanBtn.disabled = false;
                    confirmScanBtn.textContent = 'Submit';
                    confirmScanBtn.style.opacity = '1';
                }
            };
            tempImg.src = originalData;
        };

        reader.readAsDataURL(file);
    }

    function retakePhoto() {
        lastQualityResult = null;
        if (confirmScanBtn) {
            confirmScanBtn.disabled = false;
            confirmScanBtn.textContent = "Submit";
            confirmScanBtn.style.opacity = '1';
        }

        // TRIGGER UPLOAD POPUP IMMEDIATELY AS REQUESTED
        if (faceUploadInput) {
            faceUploadInput.value = '';
            faceUploadInput.click();
        }

        // Ensure UI is back to selection state if they cancel the picker 
        // (though picker is async, we can prep the view)
        const selectionStep = faceScanContainer ? faceScanContainer.querySelector('#face-scan-selection') : null;
        const cameraStep = faceScanContainer ? faceScanContainer.querySelector('#face-scan-camera') : null;

        if (selectionStep) {
            selectionStep.classList.add('active');
            selectionStep.style.display = 'flex';
        }
        if (cameraStep) {
            cameraStep.classList.remove('active');
            cameraStep.style.display = 'none';
        }

        stopCamera();
    }

    function backToSelection() {
        stopCamera();
        const selectionStep = faceScanContainer ? faceScanContainer.querySelector('#face-scan-selection') : null;
        const cameraStep = faceScanContainer ? faceScanContainer.querySelector('#face-scan-camera') : null;

        if (selectionStep) {
            selectionStep.classList.add('active');
            selectionStep.style.display = 'flex';
        }
        if (cameraStep) {
            cameraStep.classList.remove('active');
            cameraStep.style.display = 'none';
        }
    }

    function confirmPhoto() {
        if (!onFaceScanComplete || !capturedImage || !capturedImage.src) return;

        const dataToUpload = capturedImage.src;

        // Quick client-side check only; actual "image good or not" comes from face scan API in main.js
        function proceed() {
            var res = lastQualityResult;
            lastQualityResult = null;
            if (res && !res.ok) {
                showResolutionError(res.reason);
                resetSubmitButton();
                return;
            }
            if (confirmScanBtn) {
                confirmScanBtn.disabled = true;
                confirmScanBtn.textContent = "Submitting...";
            }
            // This triggers the face scan API (get-skin-analysis-from-image); main.js shows error or proceeds based on API result
            onFaceScanComplete(dataToUpload);
            if (faceUploadInput) faceUploadInput.value = '';
        }

        // Use cached result so error/success shows instantly when they click Submit
        if (lastQualityResult !== null) {
            proceed();
            return;
        }
        // Fallback if they clicked Submit before analysis finished (e.g. very fast click)
        analyzeUploadedImage(capturedImage, function (result) {
            lastQualityResult = result;
            proceed();
        });
    }

    function bindEvents() {
        const startCamBtn = faceScanContainer ? faceScanContainer.querySelector('#start-camera-btn') : null;
        if (startCamBtn) startCamBtn.onclick = activateCamera;

        if (captureBtn) captureBtn.onclick = capturePhoto;
        if (backToSelectionBtn) backToSelectionBtn.onclick = backToSelection;

        if (uploadBtn && faceUploadInput) {
            uploadBtn.onclick = () => faceUploadInput.click();
            faceUploadInput.onchange = handleFileUpload;
        }

        if (retakeBtn) retakeBtn.onclick = retakePhoto;
        if (confirmScanBtn) confirmScanBtn.onclick = confirmPhoto;

        if (skipScanBtn) {
            skipScanBtn.onclick = () => {
                stopCamera();
                if (onFaceScanCancel) onFaceScanCancel();
            };
        }
    }

    // Expose Public API
    window.ChatbotFaceScan = {
        setDOMReferences,
        setCallbacks,
        startFaceScan,
        stopCamera,
        showResolutionError,
        resetSubmitButton,
        hide: () => {
            if (faceScanContainer) {
                faceScanContainer.style.display = 'none';
                const faceScanVideo = faceScanContainer.querySelector('#face-scan-video-player');
                if (faceScanVideo) faceScanVideo.pause();
            }
            stopCamera();
        }
    };

})();

